import { useEffect } from "react";

interface JsonLdProps {
  id: string;
  schema: Record<string, unknown>;
}

/**
 * Renders a JSON-LD <script> tag imperatively (rather than via JSX) so it can
 * dedupe against a server-injected prerender copy. SSR routes (blog posts,
 * gallery, rates, etc.) inject the same schema id directly into <head> for
 * non-JS crawlers. If the React-rendered version simply appeared alongside
 * that via dangerouslySetInnerHTML, hydration would leave two <script> tags
 * with the same id in the DOM. Instead, on mount we remove any existing
 * element with this id (the server-injected one, or a stale one from a prior
 * render of this component) and insert a single fresh copy.
 */
export default function JsonLd({ id, schema }: JsonLdProps) {
  useEffect(() => {
    document.querySelectorAll(`script[id="${id}"]`).forEach((el) => el.remove());

    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [id, schema]);

  return null;
}
